/**
 * useFireworkSkeletonGate.ts
 *
 * Synchronizes skeleton reveal with a minimum delay and Firework player-ready event.
 */

import { useEffect, useState } from "react";
import { FW_PLAYER_READY_MAX_WAIT_MS } from "../constants";

/**
 * Skeleton visibility gate tied to Firework readiness.
 *
 * Reveal content only when both are true:
 * 1) minimum timer elapsed
 * 2) fw:player:ready seen (or max wait fallback reached)
 */
export function useFireworkSkeletonGate(
  enabled: boolean,
  minDelayMs: number,
  maxWaitMs: number = FW_PLAYER_READY_MAX_WAIT_MS
) {
  const [showContent, setShowContent] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setShowContent(true);
      return;
    }

    let timerDone = false;
    let playerReady = false;
    let revealed = false;

    setShowContent(false);

    const tryReveal = () => {
      if (!revealed && timerDone && playerReady) {
        revealed = true;
        setShowContent(true);
      }
    };

    const minTimer = setTimeout(() => {
      timerDone = true;
      tryReveal();
    }, minDelayMs);

    const handlePlayerReady = () => {
      playerReady = true;
      tryReveal();
    };

    document.addEventListener("fw:player:ready", handlePlayerReady);

    const maxTimer = setTimeout(() => {
      playerReady = true;
      tryReveal();
    }, maxWaitMs);

    return () => {
      clearTimeout(minTimer);
      clearTimeout(maxTimer);
      document.removeEventListener("fw:player:ready", handlePlayerReady);
    };
  }, [enabled, minDelayMs, maxWaitMs]);

  return showContent;
}
